import Header from './components/Header'
import Hero from './components/Hero'
import Footer from './components/Footer'
import NewsSection from './NewsSection'
import SEO from './components/SEO'
import ShopSection from './components/ShopSection'

// Site configuration for worldglaciersday.com
const SITE_CONFIG = {
  name: 'World Glaciers Day',
  description: 'Scientific Data & Glacier Monitoring',
  domain: 'worldglaciersday.com',
  
  hero: {
    title: 'Global Glacier Data & Research',
    subtitle: 'Scientific Monitoring & Analysis Platform',
    description: 'Access comprehensive scientific data, real-time glacier monitoring, and research tools. Explore interactive maps, climate models, and conservation analytics.',
    primaryButtonText: 'Explore Data',
    primaryButtonLink: '/data',
    secondaryButtonText: 'Research Tools',
    secondaryButtonLink: '/tools',
  },
  
  relatedSites: [
    {
      name: 'Glaciers Day',
      description: 'Official glacier day celebrations and events',
      domain: 'glaciersday.com',
    },
    {
      name: 'Glacier Education',
      description: 'Educational resources and awareness',
      domain: 'worlddayforglaciers.com',
    },
  ],
}

function App() {
  return (
    <>
      <SEO 
        title="World Glaciers Day - Global Glacier Monitoring & Data"
        description="Scientific platform for glacier monitoring, climate data, and environmental research. Access global glacier data and research insights."
        keywords="World Glaciers Day, glaciers, climate change, environment, ice, polar regions, conservation,世界冰川日,冰川,气候变化,环境,冰,极地,保护"
        type="website"
      />
      <div className="min-h-screen flex flex-col">
      <Header 
        siteName={SITE_CONFIG.name}
        siteDescription={SITE_CONFIG.description}
      />
      
      <main className="flex-grow">
        <Hero {...SITE_CONFIG.hero} />
        
        <ShopSection />
        <NewsSection 
          website="worldglaciersday"
          limit={10000}
          title="Glacier Research News"
          themeColor="indigo"
        />
        {/* Data & Research Focus Section */}
        <section className="section-padding">
          <div className="container-padding mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-glacier-900 mb-4">
                Scientific Data & Monitoring
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Access real-time glacier data, satellite imagery, and scientific analysis tools for researchers and conservationists.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">🗺️</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Interactive Glacier Maps</h3>
                <p className="text-gray-600 mb-4">
                  Explore global glacier distribution with interactive maps showing real-time changes, thickness, and retreat patterns.
                </p>
                <a href="/maps" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  View maps →
                </a>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Data Analytics</h3>
                <p className="text-gray-600 mb-4">
                  Access comprehensive datasets, climate models, and analysis tools for glacier research and conservation planning.
                </p>
                <a href="/analytics" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  Explore data →
                </a>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">🛰️</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Satellite Monitoring</h3>
                <p className="text-gray-600 mb-4">
                  Real-time satellite imagery and remote sensing data for glacier monitoring and climate change impact assessment.
                </p>
                <a href="/satellite" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  View imagery →
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* Research Tools Section */}
        <section className="section-padding bg-glacier-50">
          <div className="container-padding mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-glacier-900 mb-6">
                  Advanced Research Tools
                </h2>
                <p className="text-gray-600 mb-6">
                  Our platform provides researchers, scientists, and conservationists with cutting-edge tools for glacier analysis. From climate modeling to conservation planning, access the resources you need for meaningful research.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">✓</span>
                    </div>
                    <span className="text-gray-700">Climate change impact models</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">✓</span>
                    </div>
                    <span className="text-gray-700">Glacier mass balance calculators</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">✓</span>
                    </div>
                    <span className="text-gray-700">Real-time data visualization tools</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">✓</span>
                    </div>
                    <span className="text-gray-700">Conservation priority mapping</span>
                  </div>
                </div>
                <a href="/tools" className="btn-primary">
                  Access Research Tools
                </a>
              </div>
              <div className="bg-gradient-to-br from-glacier-100 to-glacier-200 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">📈</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">50+ Years Data</h4>
                      <p className="text-gray-600 text-sm">Historical glacier records</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🌐</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">200,000+ Glaciers</h4>
                      <p className="text-gray-600 text-sm">In our global database</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🔬</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">100+ Research Papers</h4>
                      <p className="text-gray-600 text-sm">Published using our data</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scientific Contact Section - Prominent Display */}
        <section className="section-padding bg-gradient-to-r from-glacier-700 to-glacier-900 text-white">
          <div className="container-padding mx-auto max-w-7xl text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Scientific Data & Research Inquiries
              </h2>
              <p className="text-xl text-glacier-200 mb-8">
                For research collaboration, data access requests, scientific partnerships, and technical support.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full">
                    <span className="text-3xl">🔬</span>
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-bold mb-2">Scientific Contact Email</h3>
                    <a 
                      href="mailto:info@worldglaciersday.com" 
                      className="text-2xl md:text-3xl font-bold text-white hover:text-glacier-200 transition-colors inline-block"
                    >
                      info@worldglaciersday.com
                    </a>
                    <p className="text-glacier-200 mt-3">
                      For research data access, scientific collaboration, technical support, and API integration
                    </p>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-bold mb-2">Data Access</h4>
                      <p className="text-glacier-200 text-sm">API keys, dataset requests, research permissions</p>
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">Collaboration</h4>
                      <p className="text-glacier-200 text-sm">Research partnerships, joint studies, data sharing</p>
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">Technical Support</h4>
                      <p className="text-glacier-200 text-sm">Tool integration, data processing, analysis assistance</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
      </main>
      
      <Footer 
        currentSite={{
          name: SITE_CONFIG.name,
          description: SITE_CONFIG.description,
          domain: SITE_CONFIG.domain,
        }}
        relatedSites={SITE_CONFIG.relatedSites}
      />
    </div>
    </>
  )
}

export default App